import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/order-history — rebuilt in brand primitives. Past orders as
 * branded cards with status pill, method icon, items preview, total, and
 * quick actions ("View details" → order-status route; "Order again" stub).
 *
 * Backend endpoint not wired yet, so we fall back to plausible demo orders
 * when dataService.getUserOrders() rejects. Mirrors the approach used in
 * UnifiedOrderStatus / UnifiedSafetySignals.
 */

const STATUS_META = {
  pending:   { label: 'Pending',   bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', dot: 'var(--cwm-amber)' },
  submitted: { label: 'Submitted', bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue-deep)' },
  confirmed: { label: 'Confirmed', bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue-deep)' },
  preparing: { label: 'Preparing', bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue-deep)' },
  ready:     { label: 'Ready',     bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', dot: 'var(--cwm-green)' },
  completed: { label: 'Completed', bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', dot: 'var(--cwm-green)' },
  cancelled: { label: 'Cancelled', bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   dot: 'var(--cwm-skip)' },
};

const FILTER_TABS = [
  { id: 'all',       label: 'All' },
  { id: 'completed', label: 'Completed' },
  { id: 'preparing', label: 'Active' },
  { id: 'cancelled', label: 'Cancelled' },
];

export default function UnifiedOrderHistory() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Real list from /api/orders/:user_id. Empty list → show demo
        // so the screen isn't a blank empty-state for users who've
        // never placed an order (still useful for design review).
        const data = await dataService.getUserOrders(userId);
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        if (arr.length === 0) {
          setOrders(DEMO_ORDERS);
        } else {
          const sorted = arr.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setOrders(sorted);
        }
      } catch {
        if (!cancelled) setOrders(DEMO_ORDERS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  const filtered = useMemo(() => {
    if (filter === 'all') return orders;
    if (filter === 'preparing') {
      return orders.filter((o) => ['submitted', 'confirmed', 'preparing', 'ready'].includes(o.status));
    }
    return orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <HistoryIcon size={12} /> YOUR ORDERS
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            A taste of where <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>you've been</em>.
          </h1>
          <p style={{ maxWidth: 520, margin: '0 auto', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Past orders, ready to reorder. Tap any card to track or reorder.
          </p>
        </motion.section>

        {/* Filter pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20, justifyContent: 'center' }}>
          {FILTER_TABS.map((t) => {
            const active = filter === t.id;
            const count = t.id === 'all' ? orders.length
              : t.id === 'preparing' ? orders.filter((o) => ['submitted','confirmed','preparing','ready'].includes(o.status)).length
              : orders.filter((o) => o.status === t.id).length;
            return (
              <motion.button
                key={t.id} type="button"
                onClick={() => setFilter(t.id)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 999,
                  background: active ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
                  color: active ? '#fff' : 'var(--cwm-blue-deep)',
                  border: `1.5px solid ${active ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                  cursor: 'pointer',
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 900,
                  boxShadow: active ? '0 6px 16px rgba(15,127,232,0.32)' : 'none',
                }}
              >
                {t.label}
                <span style={{
                  padding: '1px 8px', borderRadius: 999,
                  background: active ? 'rgba(255,255,255,0.24)' : 'var(--cwm-blue-pale)',
                  color: active ? '#fff' : 'var(--cwm-blue-deep)',
                  fontSize: 11, fontWeight: 900,
                }}>{count}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <OrderSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState filter={filter} onBrowse={() => navigate('/unified/discover')} />
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {filtered.map((o, i) => (
              <OrderCard key={o.id} order={o} index={i} onView={() => navigate(`/unified/order-status/${o.id}`)} onReorder={() => navigate('/unified/discover')} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function OrderCard({ order, index, onView, onReorder }) {
  const status = STATUS_META[order.status] || STATUS_META.pending;
  const date = new Date(order.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const itemsPreview = order.items.slice(0, 2).map((i) => `${i.qty}× ${i.name}`).join(', ');
  const remaining = order.items.length - 2;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 20 }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 4 }}>ORDER #{String(order.id).padStart(5, '0')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              {order.delivery_method === 'delivery' ? <TruckIcon size={18} /> : <BagIcon size={18} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {order.restaurant_name}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                <ClockIcon size={11} /> {date}
              </div>
            </div>
          </div>
        </div>
        <StatusPill status={status} />
      </header>

      <div style={{ fontSize: 14, color: 'var(--cwm-gray-700)', fontWeight: 600, marginBottom: 12 }}>
        {itemsPreview}
        {remaining > 0 && <span style={{ color: 'var(--cwm-blue-deep)', fontWeight: 800 }}> + {remaining} more</span>}
      </div>

      <footer style={{ paddingTop: 12, borderTop: '1px solid var(--cwm-gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="cwm-eyebrow" style={{ fontSize: 10, marginBottom: 2 }}>TOTAL</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>
            ${(order.total_cents / 100).toFixed(2)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onView}
                  style={{
                    padding: '8px 16px', borderRadius: 999,
                    background: '#fff', color: 'var(--cwm-blue-deep)',
                    border: '1.5px solid var(--cwm-blue-light)',
                    fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                    cursor: 'pointer',
                  }}>
            View details
          </button>
          <button type="button" onClick={onReorder}
                  style={{
                    padding: '8px 16px', borderRadius: 999,
                    background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)', color: '#fff',
                    border: 'none',
                    fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(15,127,232,0.3)',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
            <RepeatIcon size={12} /> Order again
          </button>
        </div>
      </footer>
    </motion.article>
  );
}

function StatusPill({ status }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: status.bg, color: status.fg,
      fontSize: 11, fontWeight: 900, letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: status.dot }}/>
      {status.label}
    </span>
  );
}

function OrderSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="cwm-sheet" style={{ padding: 20, opacity: 0.6 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, background: 'var(--cwm-gray-100)', borderRadius: 12 }}/>
            <div style={{ flex: 1 }}>
              <div style={{ height: 12, width: 90, background: 'var(--cwm-gray-100)', borderRadius: 6, marginBottom: 6 }}/>
              <div style={{ height: 18, width: 180, background: 'var(--cwm-gray-100)', borderRadius: 8 }}/>
            </div>
          </div>
          <div style={{ height: 14, width: '80%', background: 'var(--cwm-gray-100)', borderRadius: 6, marginBottom: 14 }}/>
          <div style={{ height: 32, background: 'var(--cwm-gray-100)', borderRadius: 999 }}/>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ filter, onBrowse }) {
  const msg = filter === 'all' ? "You haven't placed any orders yet." : `No orders in "${filter}" yet.`;
  return (
    <div className="cwm-sheet" style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: 72, height: 72, margin: '0 auto 16px', borderRadius: '50%', background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)', display: 'grid', placeItems: 'center' }}>
        <BagIcon size={32} />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 900, margin: '0 0 8px', color: 'var(--cwm-ink)' }}>{msg}</h3>
      <p style={{ fontSize: 14, color: 'var(--cwm-gray-500)', marginBottom: 20, fontWeight: 500, maxWidth: 380, margin: '0 auto 20px' }}>
        Find a restaurant, chat with its menu, and place your first order.
      </p>
      <button className="cwm-btn" onClick={onBrowse}>
        Browse restaurants
      </button>
    </div>
  );
}

// ---------- icons ----------
function HistoryIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 109-9 9 9 0 00-9 9H1l4 4 4-4H6M12 7v5l3 2"/></svg>; }
function TruckIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8h11v9H3zM14 11h4l3 3v3h-7M6 20a2 2 0 100-4 2 2 0 000 4zM18 20a2 2 0 100-4 2 2 0 000 4z"/></svg>; }
function BagIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 7h12l1 14H5zM9 7a3 3 0 016 0"/></svg>; }
function ClockIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>; }
function RepeatIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3"/></svg>; }

// ---------- demo data ----------
const DEMO_ORDERS = [
  {
    id: 42,
    restaurant_name: 'Il Violino',
    restaurant_address: '180 W 72nd St, New York, NY',
    delivery_method: 'pickup',
    status: 'preparing',
    created_at: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
    items: [
      { name: 'Gluten-Free Pappardelle', qty: 1, price_cents: 2800 },
      { name: 'Vegan Mushroom Risotto', qty: 1, price_cents: 2400 },
      { name: 'San Pellegrino', qty: 2, price_cents: 600 },
    ],
    subtotal_cents: 6400, tax_cents: 568, tip_cents: 960, total_cents: 7928,
  },
  {
    id: 38,
    restaurant_name: 'Koji Ramen',
    restaurant_address: '140 E 5th St, New York, NY',
    delivery_method: 'delivery',
    status: 'completed',
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    items: [
      { name: 'Tonkotsu (GF)', qty: 1, price_cents: 1800 },
      { name: 'Yuzu Lemonade', qty: 2, price_cents: 650 },
    ],
    subtotal_cents: 3100, tax_cents: 275, delivery_fee_cents: 500, tip_cents: 465, total_cents: 4340,
  },
  {
    id: 31,
    restaurant_name: 'Maya Mezze',
    restaurant_address: '29 Cornelia St, New York, NY',
    delivery_method: 'pickup',
    status: 'completed',
    created_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    items: [
      { name: 'Hummus & warm pita', qty: 1, price_cents: 1400 },
      { name: 'Grilled halloumi', qty: 1, price_cents: 1600 },
      { name: 'Greek salad', qty: 1, price_cents: 1200 },
      { name: 'Baklava', qty: 2, price_cents: 800 },
    ],
    subtotal_cents: 5800, tax_cents: 515, tip_cents: 870, total_cents: 7185,
  },
  {
    id: 26,
    restaurant_name: 'Little Beet Table',
    restaurant_address: '333 Park Ave S, New York, NY',
    delivery_method: 'delivery',
    status: 'cancelled',
    created_at: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    items: [{ name: 'Warm farro bowl', qty: 1, price_cents: 1800 }],
    subtotal_cents: 1800, tax_cents: 160, delivery_fee_cents: 500, tip_cents: 0, total_cents: 2460,
  },
];
